/**
 * Mana Locals Admin — AI ASSISTANCE boundary schemas.
 *
 * CONTRACT SOURCE: the API's M71 amendment A1 assistance contracts
 * (`m71-ai-agent-runtime/contracts/ai-assistance.schemas.ts`). Every shape the
 * console reads from an assistance route is declared here and parsed on the wire
 * by `src/api/client/http.ts`.
 *
 * ---------------------------------------------------------------------------
 * ONE ROUTE PER TASK
 * ---------------------------------------------------------------------------
 * The API has FIVE assistance routes, and each answers with its OWN proposal
 * shape. The task is chosen by the ROUTE, never by a member in the body, so
 * there is no `task` member to send. The response envelope is the same for all
 * five — `task`, `schemaVersion`, `requestId`, `reused`, `proposed`,
 * `missingRequiredFields`, `warnings`, `sourceReferences` — and only `proposed`
 * differs, which is why the envelope is built by a factory below.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS DELIBERATELY ABSENT
 * ---------------------------------------------------------------------------
 * No price, no stock quantity, no certification, no purity claim, no country
 * and no currency: the API's schemas carry no member for them, so a proposal
 * cannot state one. The console does not invent them either — the catalogue
 * write bodies below carry only what the catalogue API accepts.
 */
import { z } from 'zod';
import { ResponseMetaSchema } from '@/api/schemas/common';

/**
 * The descriptive-vs-variant distinction.
 *
 * `none` is descriptive: it describes a product and creates no SKU. The other
 * three are SKU-generating axes.
 */
export const AssistanceVariantAxisSchema = z.enum(['none', 'size', 'colour', 'material']);
export type AssistanceVariantAxis = z.infer<typeof AssistanceVariantAxisSchema>;

/**
 * The attribute kinds the catalogue dictionary accepts.
 *
 * There is NO number/measure kind: the domain does not have one.
 */
export const AssistanceAttributeKindSchema = z.enum([
  'text',
  'single_select',
  'multi_select',
  'boolean',
  'date',
]);
export type AssistanceAttributeKind = z.infer<typeof AssistanceAttributeKindSchema>;

/** An existing category, offered to the model so it can REUSE rather than duplicate. */
export const ExistingCategorySchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  /** The ancestor path, e.g. `Clothing > Shirts`. */
  path: z.string().min(1).max(500).optional(),
});
export type ExistingCategory = z.infer<typeof ExistingCategorySchema>;

/** An existing attribute definition, offered so the model can reuse it. */
export const ExistingAttributeSchema = z.object({
  id: z.string().min(1).max(64),
  key: z.string().min(1).max(80),
  name: z.string().min(1).max(200),
  kind: AssistanceAttributeKindSchema,
});
export type ExistingAttribute = z.infer<typeof ExistingAttributeSchema>;

/** An existing record the proposal REUSES rather than duplicating. */
export const ReusedRecordSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(200),
  /** Why it is reused. A short reason, never a paragraph. */
  reason: z.string().min(1).max(300),
});
export type ReusedRecord = z.infer<typeof ReusedRecordSchema>;

/** A field a person must supply, because the model could not and must not. */
export const MissingRequiredFieldSchema = z.object({
  field: z.string().min(1).max(200),
  reason: z.string().min(1).max(300),
});
export type MissingRequiredField = z.infer<typeof MissingRequiredFieldSchema>;

/**
 * A warning. Never an error: a warning does not block the proposal.
 *
 * The code vocabulary is CLOSED, so a caller can branch on it and a model can
 * never invent one.
 */
export const AssistanceWarningSchema = z.object({
  code: z.enum([
    'possible_duplicate',
    'ambiguous_name',
    'value_not_verifiable',
    'option_count_high',
    'axis_unclear',
    'hierarchy_depth_risk',
    'outside_requested_scope',
    'content_untrusted',
  ]),
  message: z.string().min(1).max(500),
  /** The dotted path the warning is about, when it is about one field. */
  field: z.string().min(1).max(200).optional(),
});
export type AssistanceWarning = z.infer<typeof AssistanceWarningSchema>;

/**
 * A reference to external content that WAS retrieved.
 *
 * A URL and a retrieval timestamp, and nothing else: retrieved content is
 * untrusted data and is never stored in a proposal.
 */
export const SourceReferenceSchema = z.object({
  url: z.string().min(1).max(2000),
  retrievedAt: z.string().min(1).max(64),
  title: z.string().min(1).max(300).optional(),
});
export type SourceReference = z.infer<typeof SourceReferenceSchema>;

/** One proposed category. */
export const ProposedCategorySchema = z.object({
  name: z.string().min(1).max(200),
  /** Lower kebab-case. The catalogue API refuses anything else. */
  slug: z.string().min(1).max(160),
  description: z.string().max(2000).optional(),
  /** The parent: an existing category's id, or the name of a proposed one. */
  parent: z
    .object({
      existingCategoryId: z.string().min(1).max(64).optional(),
      proposedCategoryName: z.string().min(1).max(200).optional(),
    })
    .optional(),
  variantAxis: AssistanceVariantAxisSchema.default('none'),
  uncertain: z.boolean().default(false),
});
export type ProposedCategory = z.infer<typeof ProposedCategorySchema>;

/** One proposed attribute definition. */
export const ProposedAttributeSchema = z.object({
  key: z.string().min(1).max(80),
  name: z.string().min(1).max(200),
  kind: AssistanceAttributeKindSchema,
  description: z.string().max(2000).optional(),
  variantAxis: AssistanceVariantAxisSchema.default('none'),
  options: z.array(z.string().min(1).max(120)).max(200).default([]),
  assignToCategoryIds: z.array(z.string().min(1).max(64)).max(200).default([]),
  uncertain: z.boolean().default(false),
});
export type ProposedAttribute = z.infer<typeof ProposedAttributeSchema>;

/** One proposed option value for an existing attribute. */
export const ProposedAttributeOptionSchema = z.object({
  attributeKey: z.string().min(1).max(80),
  value: z.string().min(1).max(120),
  uncertain: z.boolean().default(false),
});
export type ProposedAttributeOption = z.infer<typeof ProposedAttributeOptionSchema>;

/** One proposed SKU-generating variant option. */
export const ProposedVariantOptionSchema = z.object({
  attributeKey: z.string().min(1).max(80),
  value: z.string().min(1).max(120),
  axis: z.enum(['size', 'colour', 'material']),
  uncertain: z.boolean().default(false),
});
export type ProposedVariantOption = z.infer<typeof ProposedVariantOptionSchema>;

/** One proposed product draft field. Never a price, a stock level or a claim. */
export const ProposedProductDraftFieldSchema = z.object({
  field: z.string().min(1).max(200),
  value: z.string().min(1).max(2000),
  uncertain: z.boolean().default(false),
});
export type ProposedProductDraftField = z.infer<typeof ProposedProductDraftFieldSchema>;

/**
 * The envelope every assistance task answers with.
 *
 * Only `proposed` differs between tasks, so the envelope is built once and the
 * per-task proposal shape is supplied.
 */
function assistanceProposal<T extends z.ZodType>(proposed: T) {
  return z.object({
    task: z.string().min(1).max(80),
    schemaVersion: z.string().min(1).max(40),
    requestId: z.string().min(1).max(256),
    reused: z.array(ReusedRecordSchema).max(200).default([]),
    proposed: proposed,
    missingRequiredFields: z.array(MissingRequiredFieldSchema).max(100).default([]),
    warnings: z.array(AssistanceWarningSchema).max(100).default([]),
    sourceReferences: z.array(SourceReferenceSchema).max(20).default([]),
  });
}

export const CategoryProposalSchema = assistanceProposal(
  z.array(ProposedCategorySchema).max(200).default([]),
);
export type CategoryProposal = z.infer<typeof CategoryProposalSchema>;

export const AttributeProposalSchema = assistanceProposal(
  z.array(ProposedAttributeSchema).max(200).default([]),
);
export type AttributeProposal = z.infer<typeof AttributeProposalSchema>;

export const AttributeOptionProposalSchema = assistanceProposal(
  z.array(ProposedAttributeOptionSchema).max(500).default([]),
);
export type AttributeOptionProposal = z.infer<typeof AttributeOptionProposalSchema>;

export const VariantOptionProposalSchema = assistanceProposal(
  z.array(ProposedVariantOptionSchema).max(500).default([]),
);
export type VariantOptionProposal = z.infer<typeof VariantOptionProposalSchema>;

export const ProductDraftProposalSchema = assistanceProposal(
  z.array(ProposedProductDraftFieldSchema).max(500).default([]),
);
export type ProductDraftProposal = z.infer<typeof ProductDraftProposalSchema>;

/**
 * The HTTP envelope: `{ data: <proposal>, meta: { requestId } }`.
 */
export function AssistanceResponseSchema<T extends z.ZodType>(proposal: T) {
  return z.object({ data: proposal, meta: ResponseMetaSchema });
}

export const CategorySuggestionResponseSchema = AssistanceResponseSchema(CategoryProposalSchema);
export const AttributeSuggestionResponseSchema = AssistanceResponseSchema(AttributeProposalSchema);
export const AttributeOptionSuggestionResponseSchema = AssistanceResponseSchema(
  AttributeOptionProposalSchema,
);
export const VariantOptionSuggestionResponseSchema = AssistanceResponseSchema(
  VariantOptionProposalSchema,
);
export const ProductDraftSuggestionResponseSchema = AssistanceResponseSchema(
  ProductDraftProposalSchema,
);

/* ------------------------------------------------------------------------------------------------
 * The request bodies
 * ---------------------------------------------------------------------------------------------- */

/** The natural-language instruction. It is data, never an instruction to the system. */
const InstructionsSchema = z.string().min(3).max(4000);

export const CategorySuggestionRequestSchema = z.object({
  instructions: InstructionsSchema,
  verticalCode: z.string().min(1).max(64).optional(),
  /** The categories that already exist, so the model can reuse them. */
  existingCategories: z.array(ExistingCategorySchema).max(500).default([]),
  parentCategoryId: z.string().min(1).max(64).optional(),
  sourceUrl: z.string().min(1).max(2000).optional(),
});
export type CategorySuggestionRequest = z.infer<typeof CategorySuggestionRequestSchema>;

export const AttributeSuggestionRequestSchema = z.object({
  instructions: InstructionsSchema,
  verticalCode: z.string().min(1).max(64).optional(),
  /** The category the attributes are for, when the merchant picked one. */
  categoryId: z.string().min(1).max(64).optional(),
  /** The attributes that already exist, so the model can reuse them. */
  existingAttributes: z.array(ExistingAttributeSchema).max(500).default([]),
  sourceUrl: z.string().min(1).max(2000).optional(),
});
export type AttributeSuggestionRequest = z.infer<typeof AttributeSuggestionRequestSchema>;

export const AttributeOptionSuggestionRequestSchema = z.object({
  instructions: InstructionsSchema,
  attributeKey: z.string().min(1).max(80),
  existingOptions: z.array(z.string().min(1).max(120)).max(500).default([]),
  sourceUrl: z.string().min(1).max(2000).optional(),
});
export type AttributeOptionSuggestionRequest = z.infer<
  typeof AttributeOptionSuggestionRequestSchema
>;

export const VariantOptionSuggestionRequestSchema = z.object({
  instructions: InstructionsSchema,
  categoryId: z.string().min(1).max(64).optional(),
  existingOptions: z.array(z.string().min(1).max(120)).max(500).default([]),
  existingAxes: z.array(z.enum(['size', 'colour', 'material'])).max(10).default([]),
  sourceUrl: z.string().min(1).max(2000).optional(),
});
export type VariantOptionSuggestionRequest = z.infer<
  typeof VariantOptionSuggestionRequestSchema
>;

export const ProductDraftSuggestionRequestSchema = z.object({
  instructions: InstructionsSchema,
  categoryId: z.string().min(1).max(64).optional(),
  verticalCode: z.string().min(1).max(64).optional(),
  existingCategories: z.array(ExistingCategorySchema).max(500).default([]),
  existingAttributes: z.array(ExistingAttributeSchema).max(500).default([]),
  sourceUrl: z.string().min(1).max(2000).optional(),
});
export type ProductDraftSuggestionRequest = z.infer<typeof ProductDraftSuggestionRequestSchema>;
